import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth } from '../firebase/config';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const WARNING_BEFORE_MS = 2 * 60 * 1000;
const WARNING_COUNTDOWN_SECONDS = 120;
const TICK_INTERVAL_MS = 1000;

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click',
];

export default function IdleTimeoutManager() {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_COUNTDOWN_SECONDS);

  const lastActivityRef = useRef(Date.now());
  const warningStartRef = useRef(null);
  const tickIntervalRef = useRef(null);
  const signedOutRef = useRef(false);

  const handleSignOut = useCallback(async () => {
    if (signedOutRef.current) return;
    signedOutRef.current = true;
    try {
      sessionStorage.setItem('idleTimeout', '1');
    } catch {}
    if (tickIntervalRef.current) {
      clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }
    setShowWarning(false);
    try {
      await auth.signOut();
    } catch {}
  }, []);

  const handleStaySignedIn = () => {
    lastActivityRef.current = Date.now();
    warningStartRef.current = null;
    setShowWarning(false);
    setSecondsLeft(WARNING_COUNTDOWN_SECONDS);
  };

  useEffect(() => {
    const handleActivity = () => {
      if (warningStartRef.current !== null) return;
      lastActivityRef.current = Date.now();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    tickIntervalRef.current = setInterval(() => {
      if (signedOutRef.current) return;

      const now = Date.now();

      if (warningStartRef.current !== null) {
        const elapsedSinceWarning = now - warningStartRef.current;
        const remainingMs = WARNING_BEFORE_MS - elapsedSinceWarning;

        if (remainingMs <= 0) {
          handleSignOut();
          return;
        }

        const remainingSeconds = Math.ceil(remainingMs / 1000);
        setSecondsLeft(remainingSeconds);
        return;
      }

      const idleMs = now - lastActivityRef.current;

      if (idleMs >= IDLE_TIMEOUT_MS) {
        handleSignOut();
        return;
      }

      if (idleMs >= IDLE_TIMEOUT_MS - WARNING_BEFORE_MS) {
        warningStartRef.current = now;
        setSecondsLeft(WARNING_COUNTDOWN_SECONDS);
        setShowWarning(true);
      }
    }, TICK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
    };
  }, [handleSignOut]);

  if (!showWarning) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <div style={modalStyles.overlay} role="dialog" aria-modal="true" aria-labelledby="idle-warning-title">
      <div style={modalStyles.box}>
        <div style={modalStyles.eyebrow}>Session Notice</div>
        <h2 id="idle-warning-title" style={modalStyles.title}>Still there?</h2>
        <p style={modalStyles.message}>
          You have been inactive for a while. For your security, we will sign you out automatically when the timer runs out.
        </p>
        <div style={modalStyles.timerBox}>
          <div style={modalStyles.timerLabel}>Signing out in</div>
          <div style={modalStyles.timerValue}>{timeDisplay}</div>
        </div>
        <div style={modalStyles.buttonRow}>
          <button
            type="button"
            onClick={handleStaySignedIn}
            style={modalStyles.primaryBtn}
          >
            Stay signed in
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            style={modalStyles.secondaryBtn}
          >
            Log out now
          </button>
        </div>
      </div>
    </div>
  );
}

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(27, 42, 74, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
    fontFamily: 'DM Sans, sans-serif',
  },
  box: {
    background: '#FAF8F5',
    width: '100%',
    maxWidth: '440px',
    padding: '40px 36px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  eyebrow: {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
    color: '#C0392B',
    marginBottom: '8px',
    fontFamily: 'DM Sans, sans-serif',
  },
  title: {
    fontFamily: 'Playfair Display, serif',
    fontSize: '28px',
    fontWeight: '900',
    color: '#1B2A4A',
    lineHeight: '1.2',
    marginBottom: '14px',
    marginTop: 0,
  },
  message: {
    fontSize: '14px',
    color: '#3D3D3D',
    lineHeight: '1.6',
    marginBottom: '24px',
    fontFamily: 'DM Sans, sans-serif',
  },
  timerBox: {
    background: '#1B2A4A',
    padding: '18px 20px',
    marginBottom: '24px',
    textAlign: 'center',
  },
  timerLabel: {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: '#D4A847',
    marginBottom: '6px',
    fontFamily: 'DM Sans, sans-serif',
  },
  timerValue: {
    fontFamily: 'Playfair Display, serif',
    fontSize: '36px',
    fontWeight: '900',
    color: '#FFFFFF',
    letterSpacing: '2px',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  primaryBtn: {
    width: '100%',
    background: '#1B2A4A',
    color: '#FFFFFF',
    border: 'none',
    padding: '14px',
    fontFamily: 'Playfair Display, serif',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  secondaryBtn: {
    width: '100%',
    background: 'transparent',
    color: '#1B2A4A',
    border: '1.5px solid #1B2A4A',
    padding: '12px',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};
